import { useState, useRef, useEffect } from "react";
import api from "../api/axios";
import { useToast } from "../context/ToastContext";

export default function OTPVerify({ email, type, onSuccess, onBack, inline = false }) {
  const toast = useToast();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const refs = useRef([]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[i] = val.slice(-1);
    setOtp(next);
    if (val && i < 5) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0) refs.current[i - 1]?.focus();
    if (e.key === "Enter") handleVerify();
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (paste.length === 6) {
      setOtp(paste.split(""));
      refs.current[5]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otp.join("");
    if (code.length < 6) { toast.error("Please enter the full 6-digit code"); return; }
    setLoading(true);
    try {
      const endpoint = type === "signup" ? "/auth/verify-signup" : "/auth/verify-reset";
      const res = await api.post(endpoint, { email, otp: code });
      onSuccess(res.data);
    } catch (err) {
      const msg = err.response?.data?.message || "Invalid OTP. Please try again.";
      toast.error(msg);
      setOtp(["", "", "", "", "", ""]);
      refs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-otp", { email, type });
      toast.success("New OTP sent to your email!");
      setCountdown(60);
      setOtp(["", "", "", "", "", ""]);
      refs.current[0]?.focus();
    } catch {
      toast.error("Failed to resend OTP. Please try again.");
    } finally {
      setResending(false);
    }
  };

  // Inline form — rendered inside another container (e.g. Landing auth card)
  const formContent = (
    <>
      <button onClick={onBack} style={backBtnStyle}>
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M9 3L5 7l4 4"/></svg>
        Back
      </button>

      <div style={{ width: 48, height: 48, borderRadius: 14, background: "#e8f7f1", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
        <svg width="24" height="24" viewBox="0 0 26 26" fill="none" stroke="#1a8f68" strokeWidth="1.8" strokeLinecap="round">
          <rect x="3" y="5" width="20" height="16" rx="3"/>
          <path d="M3 9l10 6.5L23 9"/>
        </svg>
      </div>

      <div style={{ fontSize: 18, fontWeight: 800, color: "#111", marginBottom: 4 }}>Check your email</div>
      <div style={{ fontSize: 12, color: "#b0a898", marginBottom: 20, lineHeight: 1.5 }}>
        We sent a 6-digit code to{" "}
        <span style={{ color: "#1a8f68", fontWeight: 600 }}>{email}</span>
      </div>

      <div style={{ display: "flex", gap: 7, marginBottom: 20 }} onPaste={handlePaste}>
        {otp.map((digit, i) => (
          <input
            key={i}
            ref={el => refs.current[i] = el}
            maxLength={1}
            value={digit}
            onChange={e => handleChange(i, e.target.value)}
            onKeyDown={e => handleKeyDown(i, e)}
            style={{
              flex: 1, minWidth: 0, height: 52,
              border: `2px solid ${digit ? "#4ec99a" : "#e8e2d8"}`,
              borderRadius: 11, fontSize: 20, fontWeight: 800,
              color: digit ? "#1a8f68" : "#111",
              textAlign: "center",
              background: digit ? "#f4fbf8" : "#faf8f5",
              outline: "none", fontFamily: "inherit",
              transition: "border-color 0.15s, background 0.15s",
            }}
          />
        ))}
      </div>

      <button
        onClick={handleVerify}
        disabled={loading}
        style={{
          width: "100%", padding: "13px", background: loading ? "#4ec99a" : "#1a8f68",
          color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 800,
          cursor: loading ? "not-allowed" : "pointer", fontFamily: "inherit",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}
      >
        {loading ? "Verifying…" : "Verify & Continue"}
        {!loading && (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round">
            <path d="M3 8h10M9 4l4 4-4 4"/>
          </svg>
        )}
      </button>

      <div style={{ textAlign: "center", fontSize: 12, color: "#b0a898", marginTop: 14 }}>
        {countdown > 0 ? (
          <>Didn't get it? Resend in <span style={{ fontWeight: 700, color: "#e8900a" }}>{countdown}s</span></>
        ) : (
          <button onClick={handleResend} disabled={resending} style={{ background: "none", border: "none", color: "#1a8f68", fontWeight: 700, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>
            {resending ? "Sending…" : "Resend code"}
          </button>
        )}
      </div>
    </>
  );

  // Inline mode — just the form, no page wrapper
  if (inline) return <div>{formContent}</div>;

  // Full-page mode — with hero panel (used standalone)
  return (
    <div style={pageStyle}>
      <div style={splitStyle}>
        {/* RIGHT — FORM */}
        <div style={formPanelStyle}>{formContent}</div>

        {/* LEFT — HERO */}
        <div style={heroPanelStyle}>
          <div style={wmStyle}>تأیید</div>
          <div style={eyebrowStyle}>Almost there</div>
          <div style={headlineStyle}>
            Your Persian<br />journey is about<br />
            to <span style={{ color: "#1a8f68" }}>begin.</span>
          </div>
          <div style={bodyTxtStyle}>One quick verification and you're all set. Check your inbox for the code we sent.</div>

          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            <StepItem icon="check" label="Create your account" done />
            <StepLine done />
            <StepItem icon="2" label="Verify your email" active />
            <StepLine />
            <StepItem icon="3" label="Start learning Persian" pending />
          </div>
        </div>
      </div>
    </div>
  );
}

function StepItem({ icon, label, done, active, pending }) {
  const bg = done ? "#1a8f68" : active ? "#e8f7f1" : "#f5f0e8";
  const border = done ? "none" : active ? "2px solid #1a8f68" : "2px solid #e8e2d8";
  const color = done ? "#fff" : active ? "#1a8f68" : "#c0b8ae";
  const textColor = pending ? "#c0b8ae" : "#111";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ width: 28, height: 28, borderRadius: "50%", background: bg, border, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {done ? (
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"><path d="M2.5 6.5l3 3 5-5"/></svg>
        ) : (
          <span style={{ fontSize: 12, fontWeight: 800, color }}>{icon}</span>
        )}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: textColor }}>{label}</div>
    </div>
  );
}

function StepLine({ done }) {
  return <div style={{ width: 2, height: 14, background: done ? "#1a8f68" : "#e8e2d8", marginLeft: 13 }} />;
}

const pageStyle = { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f0ebe2", padding: 24 };
const splitStyle = { width: "100%", maxWidth: 820, height: 520, borderRadius: 24, overflow: "hidden", display: "flex", flexDirection: "row-reverse", border: "1px solid #ddd8cf", boxShadow: "0 8px 40px rgba(0,0,0,0.09)" };
const formPanelStyle = { width: 380, flexShrink: 0, background: "#faf8f5", padding: "44px 40px", display: "flex", flexDirection: "column", justifyContent: "center", borderLeft: "1px solid #ede8df" };
const heroPanelStyle = { flex: 1, background: "#fff8f0", padding: "52px 48px", display: "flex", flexDirection: "column", justifyContent: "center", position: "relative", overflow: "hidden" };
const wmStyle = { position: "absolute", right: -10, top: 5, fontSize: 145, color: "rgba(26,143,104,0.055)", fontWeight: 900, lineHeight: 1, userSelect: "none", letterSpacing: -4 };
const eyebrowStyle = { fontSize: 11, fontWeight: 700, color: "#1a8f68", letterSpacing: 2, textTransform: "uppercase", marginBottom: 12, position: "relative" };
const headlineStyle = { fontSize: 34, fontWeight: 900, color: "#111", lineHeight: 1.1, letterSpacing: -1, marginBottom: 14, position: "relative" };
const bodyTxtStyle = { fontSize: 13, color: "#999", lineHeight: 1.7, marginBottom: 28, position: "relative" };
const backBtnStyle = { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: "#b0a898", cursor: "pointer", marginBottom: 20, background: "none", border: "none", fontFamily: "inherit", padding: 0 };
